/**
 * dailyCountdown.ts
 *
 * Pure helper that computes the time remaining until the GMT+3 day close
 * (23:59:59 GMT+3 of the given day key) and returns a formatted label.
 *
 * Format: "XH YMIN"  (e.g. "6H 24MIN", "0H 30MIN")
 *
 * Design (D for Dependency Inversion):
 * - `now` is injected as an optional millisecond timestamp, defaulting to
 *   Date.now(). This makes the function fully deterministic in unit tests.
 * - No side effects, no imports — pure computation only (S: Single Responsibility).
 */

/**
 * GMT+3 offset in milliseconds.
 * 3 hours * 60 min * 60 sec * 1000 ms
 */
const GMT_PLUS_3_MS = 3 * 60 * 60 * 1000;

/**
 * Compute the UTC timestamp of the day close (23:59:59 GMT+3) for a given day key.
 *
 * GMT+3 23:59:59 = UTC 20:59:59
 */
function dayCloseUtcMs(day: string): number {
  // Parse the day key as a UTC midnight first, then shift to GMT+3 close time.
  // GMT+3 23:59:59 = UTC (23:59:59 - 3h) = UTC 20:59:59
  const utcMidnight = Date.parse(`${day}T00:00:00.000Z`);
  // + 20h 59m 59s in ms = (23*3600 + 59*60 + 59) * 1000 - GMT_PLUS_3_MS
  // = UTC midnight + (23:59:59 as seconds * 1000) - 3h offset
  const secondsInDay = 23 * 3600 + 59 * 60 + 59;
  return utcMidnight + secondsInDay * 1000 - GMT_PLUS_3_MS;
}

/**
 * Returns the countdown label from `now` to the GMT+3 day close of `day`.
 *
 * @param day  - YYYY-MM-DD (GMT+3 day key, as returned by the /me API)
 * @param now  - Current time in ms since epoch. Defaults to Date.now().
 * @returns    - Formatted label "XH YMIN". Returns "0H 0MIN" when past close.
 */
export function computeCountdownLabel(day: string, now: number = Date.now()): string {
  const closeMs = dayCloseUtcMs(day);
  const remainingMs = Math.max(0, closeMs - now);

  const totalMinutes = Math.floor(remainingMs / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return `${hours.toString()}H ${minutes.toString()}MIN`;
}
